/**
 * @fileoverview The short list of MCP servers the console offers as one-click
 * connections (`plans/mcp-oauth-support.md` §8, §3E).
 *
 * A CONSTANT, not an endpoint. The list is purely presentational — what gets
 * saved is still just an address and a sign-in kind — so building an API for
 * five entries would be infrastructure for its own sake. The trade-off is that
 * the agent cannot read the catalog to suggest connections, which is moot: it
 * cannot complete a sign-in either way.
 *
 * WHY THIS IS NOT AUTO-POPULATED from a public directory, despite mcpservers.org
 * listing 9,800+ entries. There is no documented machine-readable feed; entries
 * carry no authentication metadata, so we could not tell which form to render;
 * the "official" marking is their vetting standard rather than ours; and most
 * entries are stdio servers you run locally, which this client cannot speak at
 * all. Scraping one would produce a list where the majority of picks fail while
 * implying we had checked them.
 *
 * The bar for adding an entry is deliberately low-ceremony and high-evidence:
 * SOMEONE HAS CONNECTED IT SUCCESSFULLY. That is why `verified` exists — an
 * unverified entry still shows, but wears an "experimental" badge rather than
 * quietly implying it works.
 */

export interface McpCatalogEntry {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly url: string;
  readonly auth: 'oauth' | 'header' | 'none';
  /** Only for the fixed-value kind — which header the token goes in. */
  readonly headerName?: string;
  readonly docsUrl?: string;
  /** false ⇒ shows an "experimental" badge. Set true only after a real connect. */
  readonly verified: boolean;
}

/**
 * Seed entries.
 *
 * Every one is `verified: false` today because none has been connected
 * end-to-end yet — the flag is evidence, not optimism, and flipping it is a
 * one-line change once someone has actually signed in.
 */
export const MCP_CATALOG: readonly McpCatalogEntry[] = [
  {
    id: 'linear',
    name: 'Linear',
    description: 'Issues, projects and cycles.',
    url: 'https://mcp.linear.app/mcp',
    auth: 'oauth',
    docsUrl: 'https://linear.app/docs/mcp',
    verified: false,
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'Pages and databases in your workspace.',
    url: 'https://mcp.notion.com/mcp',
    auth: 'oauth',
    docsUrl: 'https://developers.notion.com/docs/mcp',
    verified: false,
  },
  {
    id: 'sentry',
    name: 'Sentry',
    description: 'Errors, issues and releases.',
    url: 'https://mcp.sentry.dev/mcp',
    auth: 'oauth',
    docsUrl: 'https://docs.sentry.io/product/sentry-mcp/',
    verified: false,
  },
  {
    id: 'robinhood',
    name: 'Robinhood',
    description: 'Trading and portfolio tools.',
    url: 'https://agent.robinhood.com/mcp/trading',
    auth: 'oauth',
    docsUrl: 'https://robinhood.com/us/en/support/articles/robinhood-mcp/',
    // Robinhood restricts which return addresses it accepts, and a hosted
    // product like ours is the shape independently reported as refused at the
    // approval step (§10). It ships as an experiment; nothing else depends on
    // it, and only admin/test accounts can reach Maximus at all.
    verified: false,
  },
];

/** Look up a catalog entry by id. */
export function catalogEntry(id: string): McpCatalogEntry | undefined {
  return MCP_CATALOG.find((entry) => entry.id === id);
}

/**
 * The two constraints that must appear in the custom-server form, because
 * neither is obvious from the public directories (§8).
 *
 * 1. We speak Streamable HTTP only — no stdio, no locally-run commands. The
 *    large majority of directory entries are repositories you run on your own
 *    machine and CANNOT be connected here.
 * 2. A listing is not an endorsement. An MCP server can take actions on your
 *    behalf; anything outside this catalog is connected at your own risk.
 */
export const CUSTOM_SERVER_NOTICE = {
  transport:
    'Only remote servers work here — the address must start with https://. ' +
    'Servers you run locally on your own machine (stdio) cannot be connected.',
  risk:
    'Connecting a server lets it take actions on your behalf. We have not ' +
    'reviewed servers outside the list above — add them at your own risk.',
  directoryUrl: 'https://mcpservers.org/',
} as const;

/** True when an address is one this client can actually speak to. */
export function isConnectableUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  // https only: a token or an OAuth bearer would otherwise cross the network in
  // clear text, and every catalog provider requires TLS regardless.
  return parsed.protocol === 'https:';
}
